import * as React from "react"

import SimpleModal from "@/shadcn/components/SimpleModal"
import { ChangeSummary } from "./ChangeSummary"
import { number } from "./format"

/**
 * Shown once a paid plan starts: to a new subscriber, or to one coming back. Plans
 * with a faster source-check speed offer to turn it on for every source. Figma:
 * S311lGXE6JSyTnNAXHmtR7, section "After purchase · new and returning subscribers"
 * (30263:4706): new 30263:4707, updating 30263:5374, updated 30263:5806, Basic
 * 30263:5987, returning 30263:6169.
 *
 * The frames draw the speed row as "Every 30 min → Every 5 min". Sources each keep
 * their own speed, so there's no single "before" to show; the row states the new one.
 */

type WelcomeDialogProps = {
  returning: boolean
  /** The plan's name without "dlvr.it", e.g. "Pro". */
  label: string
  /**
   * The plan's fastest source check, in minutes, to offer turning on. `null` for a
   * plan with nothing to offer (Basic, Starter), which says what it includes instead.
   */
  fastest: number | null
  limits: { inputs: number; outputs: number; postLimit: number }
  speed: "offered" | "updating" | "updated"
  onSpeedUp: () => void
  /** Keep the current speed, or acknowledge: both carry on to where the user was going. */
  onDone: () => void
  onClose: () => void
}

export default function WelcomeDialog({
  returning,
  label,
  fastest,
  limits,
  speed,
  onSpeedUp,
  onDone,
  onClose,
}: WelcomeDialogProps) {
  if (fastest === null) {
    return (
      <SimpleModal
        title={`You're on dlvr.it ${label}`}
        description={`You can now post from ${number(limits.inputs)} sources to ${number(limits.outputs)} destinations${
          limits.postLimit > 0 ? `, up to ${number(limits.postLimit)} posts a month` : ""
        }.`}
        cancelLabel={null}
        confirmLabel="Go to automations"
        onConfirm={onDone}
        onClose={onClose}
      />
    )
  }

  if (speed === "updated") {
    return (
      <SimpleModal
        title={`Your sources are checked every ${fastest} minutes`}
        description="New items will post sooner. You can change this for each source in its settings."
        cancelLabel={null}
        confirmLabel="Done"
        onConfirm={onDone}
        onClose={onClose}
      />
    )
  }

  const offer = `${label} can check your sources every ${fastest} minutes`

  return (
    <SimpleModal
      title={returning ? `Welcome back to dlvr.it ${label}` : `You're on dlvr.it ${label}`}
      description={
        returning
          ? `Your automations are posting again. ${offer} so new items post sooner.`
          : `${offer}, so new items post sooner. Want us to turn that on?`
      }
      cancelLabel="Keep current speed"
      confirmLabel={`Check every ${fastest} minutes`}
      busy={speed === "updating"}
      busyLabel="Updating your sources"
      onConfirm={onSpeedUp}
      onCancel={onDone}
      onClose={onClose}
    >
      <ChangeSummary rows={[{ label: "Source checks", to: `Every ${fastest} min` }]} />
    </SimpleModal>
  )
}
